/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { useAtom } from 'jotai'
import React, { useMemo } from 'react'

import { selectedCountryAtom } from '@/store/country'
import { countries, getCountryByCode } from '@/utils/countries'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type CountrySelectProps = {
  /** Optional identifier for the input element (default: `country-select`). */
  id?: string

  /** Optional class name(s) to apply to the select element. */
  className?: string

  /** Whether the select dropdown is disabled. */
  disabled?: boolean
}

/**
 * A select element to choose a country. Designed to be used in the footer.
 */
const CountrySelect: React.FC<CountrySelectProps> = ({ id, className, disabled = false }) => {
  const [selectedCountry, setSelectedCountry] = useAtom(selectedCountryAtom)

  /** Memo-ised version of all the country options. */
  const countryOptions = useMemo(() => {
    return countries.map((country) => (
      <option key={country.code} value={country.code}>
        {country.name}
      </option>
    ))
  }, [countries])

  /**
   * Handle the user selecting a new country from the select element.
   *
   * @param event - the change event from the select element
   */
  const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCode = event.target.value
    const newCountry = getCountryByCode(selectedCode)

    if (newCountry) {
      setSelectedCountry(newCountry)
    }
  }

  return (
    <select
      id={id ?? 'country-select'}
      className={cn('disabled:bg-gray-200 disabled:text-gray-800 disabled:cursor-not-allowed', className)}
      value={selectedCountry.code}
      onChange={handleCountryChange}
      disabled={disabled}
    >
      {countryOptions}
    </select>
  )
}

export { CountrySelect }
